import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import AdminPageHeader from "./AdminPageHeader";

export default function HRPartnersAdmin() {
  const { readOnly } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState({ full_name: "", email: "" });
  const [showCreated, setShowCreated] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/hr-partners");
      setRows(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const patch = async (id, body) => {
    if (readOnly) return;
    try {
      await api.patch(`/admin/hr-partners/${id}`, body);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const add = async () => {
    if (readOnly || !adding.full_name.trim() || !adding.email.trim()) return;
    try {
      const { data } = await api.post("/admin/hr-partners", adding);
      setShowCreated(data);
      setAdding({ full_name: "", email: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="admin-hr-partners-page" className="space-y-5">
      <AdminPageHeader
        eyebrow="Admin · HR partners"
        title="HR partners"
        description="Manage HR-role users. Created accounts receive a one-time temporary password."
      />
      {showCreated && (
        <div
          data-testid="hr-partner-created-banner"
          className="rounded-xl p-4"
          style={{ backgroundColor: "rgba(232,203,142,0.18)", border: "1px solid #E8CB8E" }}
        >
          <p className="text-sm text-[color:var(--page-fg)]">
            <b>{showCreated.full_name}</b> created. Share these credentials securely:
          </p>
          <p className="mt-2 font-mono text-sm text-[color:var(--page-fg)]">
            email: {showCreated.email} · temp password: <b>{showCreated.temp_password}</b>
          </p>
          <Button variant="outline" size="sm" onClick={() => setShowCreated(null)} className="mt-2">
            Got it
          </Button>
        </div>
      )}
      {!readOnly && (
        <div className="brand-card p-4">
          <h2 className="text-sm font-semibold">Add HR partner</h2>
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <Input
              value={adding.full_name}
              onChange={(e) => setAdding((a) => ({ ...a, full_name: e.target.value }))}
              placeholder="Full name"
              className="w-[220px]"
              aria-label="Full name"
              data-testid="hr-partner-add-name"
            />
            <Input
              value={adding.email}
              onChange={(e) => setAdding((a) => ({ ...a, email: e.target.value }))}
              placeholder="email@goodwinliving.org"
              className="w-[280px]"
              aria-label="Email"
              data-testid="hr-partner-add-email"
            />
            <Button
              onClick={add}
              data-testid="hr-partner-add-submit"
              className="goodwin-primary-btn text-white"
            >
              <Plus className="mr-1 h-4 w-4" />
              Create
            </Button>
          </div>
        </div>
      )}
      <div className="brand-card overflow-hidden">
        <table className="w-full text-sm">
          <caption className="sr-only">HR partners</caption>
          <thead className="bg-[color:var(--surface-2)] text-left text-xs uppercase tracking-wider text-[color:var(--page-fg-muted)]">
            <tr>
              <th className="px-4 py-3" scope="col">Name</th>
              <th className="px-4 py-3" scope="col">Email</th>
              <th className="px-4 py-3" scope="col">Source</th>
              <th className="px-4 py-3" scope="col">Open cases</th>
              <th className="px-4 py-3" scope="col">Admin</th>
              <th className="px-4 py-3" scope="col">Active</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--border-subtle)]">
            {rows.map((p) => {
              // Phase 2 (May 2026) — admins that surface here only because
              // they're admin users (no dedicated HR-partner record) shouldn't
              // be deletable / toggle-able from this page. User management
              // owns those switches.
              const isAdminOnly = p.source === "admin_user";
              return (
              <tr key={p.id} data-testid={`hr-partner-row-${p.id}`}>
                <td className="px-4 py-3 text-[color:var(--page-fg)]">{p.full_name}</td>
                <td className="px-4 py-3 text-[color:var(--page-fg-secondary)]">{p.email}</td>
                <td className="px-4 py-3">
                  {isAdminOnly ? (
                    <span
                      data-testid={`hr-partner-source-admin-${p.id}`}
                      className="inline-flex items-center rounded-full bg-[color:var(--surface-2)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--page-fg-secondary)]"
                      title="This person is listed because they're an admin. Manage them in User Management."
                    >
                      Admin
                    </span>
                  ) : (
                    <span className="text-[11px] text-[color:var(--page-fg-muted)]">HR Partner</span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-[color:var(--page-fg)]">{p.open_cases}</td>
                <td className="px-4 py-3">
                  <Switch
                    checked={!!p.is_admin}
                    onCheckedChange={(v) => patch(p.id, { is_admin: v })}
                    disabled={readOnly || isAdminOnly}
                    aria-label={`Toggle ${p.full_name} admin`}
                  />
                </td>
                <td className="px-4 py-3">
                  <Switch
                    checked={p.active !== false}
                    onCheckedChange={(v) => patch(p.id, { active: v })}
                    disabled={readOnly || isAdminOnly}
                    aria-label={`Toggle ${p.full_name} active`}
                  />
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
